import StaffColumn from '@/components/board/staff-column';
import TaskCard from '@/components/board/task-card';
import TaskDropZone from '@/components/board/task-drop-zone';
import Sidebar from '@/components/layout/sidebar';
import HelpRequestModal from '@/components/modals/help-request-modal';
import NeedHelpModal from '@/components/modals/need-help-modal';
import PrintDialog from '@/components/modals/print-dialog';
import StaffModal from '@/components/modals/staff-modal';
import TaskModal from '@/components/modals/task-modal';
import TaskViewModal from '@/components/modals/task-view-modal';
import NotificationBell from '@/components/notification-bell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUnreadComments } from '@/contexts/UnreadCommentsContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket } from '@/hooks/useWebSocket';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import type {
  Project,
  TaskWithRelations,
  TeamWithMembers,
  UserWithTeam,
} from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  FolderOpen,
  LayoutGrid,
  Plus,
  Search,
  User,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

export default function Board() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Enable WebSocket for real-time updates
  const { isConnected } = useWebSocket();
  const { hasUnreadComments } = useUnreadComments();

  // Listen for task-reopened event (assignee) and refetch so board updates in real time
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { assigneeId?: string }
        | undefined;
      if (detail?.assigneeId && detail.assigneeId === user?.id) {
        queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
        queryClient.invalidateQueries({
          queryKey: ['/api/users', user.id, 'tasks'],
        });
        void queryClient.refetchQueries({ queryKey: ['/api/tasks'] });
        void queryClient.refetchQueries({
          queryKey: ['/api/users', user.id, 'tasks'],
        });
      }
    };
    window.addEventListener('task-reopened', handler);
    return () => window.removeEventListener('task-reopened', handler);
  }, [user?.id, queryClient]);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithRelations | null>(
    null
  );
  const [viewingTask, setViewingTask] = useState<TaskWithRelations | null>(
    null
  );
  const [requestingExtension, setRequestingExtension] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userDropdownSearch, setUserDropdownSearch] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [gmViewMode, setGmViewMode] = useState<'my' | 'viewer' | 'created'>(
    'my'
  ); // For GM/PM/HOD: 'my' = assigned tasks, 'viewer' = viewer tasks (GM only), 'created' = tasks created by me
  const [isHelpRequestModalOpen, setIsHelpRequestModalOpen] = useState(false);
  const [preselectedAssigneeId, setPreselectedAssigneeId] = useState<
    string | undefined
  >(undefined);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [needHelpTask, setNeedHelpTask] = useState<TaskWithRelations | null>(
    null
  );
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<
    'all' | 'team' | 'project'
  >('all');
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<
    | 'manager'
    | 'manager_team'
    | 'staff'
    | 'project'
    | 'admin'
    | 'admin_team'
    | 'my'
  >('manager');
  const [selectedStaff, setSelectedStaff] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [adminFilterType, setAdminFilterType] = useState<
    'all' | 'manager' | 'staff' | 'project'
  >('all');
  const [adminSelectedManager, setAdminSelectedManager] = useState<
    string | null
  >(null);
  const [adminSelectedStaff, setAdminSelectedStaff] = useState<string | null>(
    null
  );
  const [adminSelectedProject, setAdminSelectedProject] = useState<
    string | null
  >(null);

  // Show/hide completed tasks toggle
  const [showCompletedTasks, setShowCompletedTasks] = useState<boolean>(false);
  // Show all users toggle (default false - only show users with active tasks)
  const [showAllUsers, setShowAllUsers] = useState<boolean>(false);

  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Global task collapse state
  const [allTasksCollapsed, setAllTasksCollapsed] = useState<boolean>(false);

  // Temporary debug overlay toggle (default OFF, persisted)
  const [showManagerDebug, setShowManagerDebug] = useState<boolean>(false);
  useEffect(() => {
    // Allow overriding via URL (?debug=1 to force ON, ?debug=0 to force OFF)
    const params = new URLSearchParams(window.location.search);
    const urlDebug = params.get('debug');
    const stored = window.localStorage.getItem('sb_show_manager_debug');

    if (urlDebug === '1') {
      setShowManagerDebug(true);
      window.localStorage.setItem('sb_show_manager_debug', 'true');
    } else if (urlDebug === '0') {
      setShowManagerDebug(false);
      window.localStorage.setItem('sb_show_manager_debug', 'false');
    } else if (stored === 'true' || stored === 'false') {
      setShowManagerDebug(stored === 'true');
    } else {
      // default OFF when no prior preference
      setShowManagerDebug(false);
    }
  }, []);
  useEffect(() => {
    window.localStorage.setItem(
      'sb_show_manager_debug',
      String(showManagerDebug)
    );
  }, [showManagerDebug]);

  // Persist completed tasks toggle
  useEffect(() => {
    if (!user) return;
    const key = `sb_show_completed_tasks_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setShowCompletedTasks(stored === 'true');
    } else {
      // Default to false (hide completed tasks) for new users
      setShowCompletedTasks(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const key = `sb_show_completed_tasks_${user.id}`;
    window.localStorage.setItem(key, String(showCompletedTasks));
  }, [showCompletedTasks, user?.id]);

  // Persist sidebar collapse state
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setIsSidebarCollapsed(stored === 'true');
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(isSidebarCollapsed));
  }, [isSidebarCollapsed, user?.id]);

  // Persist global task collapse state
  useEffect(() => {
    if (!user) return;
    const key = `sb_all_tasks_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setAllTasksCollapsed(stored === 'true');
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const key = `sb_all_tasks_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(allTasksCollapsed));
  }, [allTasksCollapsed, user?.id]);

  // Team member team-view toggle
  const [showTeamView, setShowTeamView] = useState(false);

  // Persist admin team selection for Admin Team View
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const key = `sb_admin_team_view_team_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored) {
      setSelectedTeam(stored);
    }
  }, [user?.id]);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const key = `sb_admin_team_view_team_${user.id}`;
    try {
      if (viewMode === 'admin_team' && selectedTeam) {
        window.localStorage.setItem(key, selectedTeam);
      }
    } catch {}
  }, [viewMode, selectedTeam, user?.id]);
  useEffect(() => {
    if (user?.role === 'team_member' && (user as any).allowTeamView) {
      const stored = window.localStorage.getItem(
        'sb_team_member_show_team_view'
      );
      if (stored === 'true' || stored === 'false') {
        setShowTeamView(stored === 'true');
      } else {
        setShowTeamView(true); // default ON when allowed
      }
      // Ensure team context is set so manager-style view shows all teammates
      setSelectedTeam(user.teamId || null);
    } else {
      setShowTeamView(false);
    }
  }, [user]);
  useEffect(() => {
    if (user?.role === 'team_member' && (user as any).allowTeamView) {
      window.localStorage.setItem(
        'sb_team_member_show_team_view',
        String(showTeamView)
      );
      // Keep team context in sync
      if (showTeamView) setSelectedTeam(user?.teamId || null);
    }
  }, [showTeamView, user]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
    }
  }, [user, authLoading, toast]);

  // Removed global 'n' shortcut for creating tasks due to conflicts

  // Set appropriate view mode and selected staff for team members and staff
  useEffect(() => {
    if (user && (user.role === 'team_member' || user.role === 'staff')) {
      setViewMode('my');
      setSelectedStaff(user.id);
    } else if (user && user.role === 'admin') {
      setViewMode('admin');
    } else if (user && user.role === 'manager') {
      setViewMode('manager');
    } else if (
      user &&
      (user.role === 'gm' ||
        user.role === 'pm' ||
        user.role === 'hod' ||
        user.role === 'dpm')
    ) {
      // GM, PM, HOD only use "My Board" view - no other view options
      // Force them to "My Board" and prevent switching to other views
      if (viewMode !== 'my') {
        setViewMode('my');
        setSelectedStaff(user.id);
      }
    }
  }, [user, viewMode]);

  // Fetch data
  // Read filter from URL (?filter=...)
  const initialFilter = new URLSearchParams(window.location.search).get(
    'filter'
  );
  const [boardFilter, setBoardFilter] = useState<string | null>(initialFilter);

  // Managers/Admins can toggle between global (team scope) vs personal (my tasks + help) view
  const [boardPersonal, setBoardPersonal] = useState(false);
  // My Board local filter for team members
  const [myFilter, setMyFilter] = useState<
    'all' | 'pending' | 'started' | 'completed'
  >('all');

  // Saved Views
  type SavedView = {
    name: string;
    boardFilter: string | null;
    boardPersonal: boolean;
    selectedTeam: string | null;
    selectedProject: string | null;
    selectedStaff: string | null;
    viewMode: typeof viewMode;
    adminFilterType: typeof adminFilterType;
    adminSelectedManager: string | null;
    adminSelectedStaff: string | null;
    adminSelectedProject: string | null;
  };
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [selectedView, setSelectedView] = useState<string>('');

  // Auto-restore last-used board state per user
  useEffect(() => {
    if (!user) return;
    // For staff, always use enforced defaults (My Board) and do not restore saved manager/project views
    if (user.role === 'staff') return;
    const key = `sb_board_last_state_${user.id}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        if (user.role === 'admin') setBoardPersonal(true);
        return;
      }
      const s = JSON.parse(raw);
      if (s.viewMode) setViewMode(s.viewMode);
      if (user.role === 'admin') {
        setBoardPersonal(true);
      } else if (typeof s.boardPersonal === 'boolean') {
        setBoardPersonal(s.boardPersonal);
      }
      if (s.selectedTeam !== undefined) setSelectedTeam(s.selectedTeam);
      if (s.selectedProject !== undefined)
        setSelectedProject(s.selectedProject);
      if (s.selectedStaff !== undefined) setSelectedStaff(s.selectedStaff);
      if (s.boardFilter !== undefined) setBoardFilter(s.boardFilter);
      if (s.adminFilterType !== undefined)
        setAdminFilterType(s.adminFilterType);
      if (s.adminSelectedManager !== undefined)
        setAdminSelectedManager(s.adminSelectedManager);
      if (s.adminSelectedStaff !== undefined)
        setAdminSelectedStaff(s.adminSelectedStaff);
      if (s.adminSelectedProject !== undefined)
        setAdminSelectedProject(s.adminSelectedProject);
    } catch {}
  }, [user?.id]);

  const didInitPersistRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (!didInitPersistRef.current) {
      // avoid TDZ/order hiccups on first render
      didInitPersistRef.current = true;
      return;
    }
    try {
      const key = `sb_board_last_state_${user.id}`;
      const snapshot = {
        viewMode,
        boardPersonal,
        selectedTeam,
        selectedProject,
        selectedStaff,
        boardFilter,
        adminFilterType,
        adminSelectedManager,
        adminSelectedStaff,
        adminSelectedProject,
      };
      localStorage.setItem(key, JSON.stringify(snapshot));
    } catch {}
  }, [
    user?.id,
    viewMode,
    boardPersonal,
    selectedTeam,
    selectedProject,
    selectedStaff,
    boardFilter,
    adminFilterType,
    adminSelectedManager,
    adminSelectedStaff,
    adminSelectedProject,
  ]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('sb_board_saved_views');
      if (raw) setSavedViews(JSON.parse(raw));
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('sb_board_saved_views', JSON.stringify(savedViews));
    } catch {}
  }, [savedViews]);

  const saveCurrentView = () => {
    const name = prompt('Save view as:');
    if (!name) return;
    const view: SavedView = {
      name,
      boardFilter,
      boardPersonal,
      selectedTeam,
      selectedProject,
      selectedStaff,
      viewMode,
      adminFilterType,
      adminSelectedManager,
      adminSelectedStaff,
      adminSelectedProject,
    } as any;
    setSavedViews(prev => {
      const without = prev.filter(v => v.name !== name);
      return [...without, view];
    });
    setSelectedView(name);
  };

  const applySavedView = (name: string) => {
    const view = savedViews.find(v => v.name === name);
    if (!view) return;
    setSelectedView(name);
    setBoardFilter(view.boardFilter);
    setBoardPersonal(view.boardPersonal);
    setSelectedTeam(view.selectedTeam);
    setSelectedProject(view.selectedProject);
    setSelectedStaff(view.selectedStaff);
    setViewMode(view.viewMode);
    setAdminFilterType(view.adminFilterType);
    setAdminSelectedManager(view.adminSelectedManager);
    setAdminSelectedStaff(view.adminSelectedStaff);
    setAdminSelectedProject(view.adminSelectedProject);
  };

  const deleteSavedView = (name: string) => {
    setSavedViews(prev => prev.filter(v => v.name !== name));
    if (selectedView === name) setSelectedView('');
  };

  // Keep URL in sync when filter changes
  const applyFilter = (next: string | null) => {
    setBoardFilter(next);
    const params = new URLSearchParams(window.location.search);
    if (next) params.set('filter', next);
    else params.delete('filter');
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  };

  // Persist personal toggle per user
  useEffect(() => {
    if (!user) return;
    const key = `sb_board_personal_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setBoardPersonal(stored === 'true');
    }
  }, [user?.id]);
  useEffect(() => {
    if (!user) return;
    const key = `sb_board_personal_${user.id}`;
    window.localStorage.setItem(key, String(boardPersonal));
  }, [boardPersonal, user?.id]);

  // Pagination state
  const [taskPage, setTaskPage] = useState<{
    items: TaskWithRelations[];
    nextCursor: string | null;
    isLoadingMore?: boolean;
  }>({ items: [], nextCursor: null, isLoadingMore: false });
  const [initialLoaded, setInitialLoaded] = useState(false);

  const teamCtx = useMemo(() => {
    if (
      user &&
      user.role === 'team_member' &&
      (user as any).allowTeamView &&
      viewMode === 'manager'
    ) {
      return selectedTeam || user.teamId || null;
    }
    return null;
  }, [
    user?.id,
    (user as any)?.allowTeamView,
    user?.teamId,
    viewMode,
    selectedTeam,
  ]);

  const { data: tasksResp, isLoading: tasksLoading } = useQuery<
    | { items?: TaskWithRelations[]; nextCursor?: string | null }
    | TaskWithRelations[]
  >({
    queryKey: [
      '/api/tasks',
      {
        filter: boardFilter,
        personal: boardPersonal,
        uid: user?.id,
        role: user?.role,
        viewMode,
        teamCtx,
        selectedProject,
      },
    ],
    retry: false,
    // No polling: refetch only when WebSocket invalidates (any notification)
    refetchInterval: false,
    queryFn: async () => {
      // For managers/admins, if personal toggle is ON, fetch via user-specific endpoint
      if (
        user &&
        (user.role === 'manager' || user.role === 'admin') &&
        boardPersonal
      ) {
        const res = await fetch(`/api/users/${user.id}/tasks`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
        return await res.json();
      }
      const params = new URLSearchParams();
      // In Project View, always fetch all statuses for the selected project and do not paginate
      if (viewMode !== 'project' && boardFilter)
        params.set('filter', boardFilter);
      if (viewMode !== 'project') params.set('limit', '50');
      // Project view: if a team project is selected, scope to that team (applies to all roles)
      // Use teamId instead of projectId since we're showing team projects (teams)
      if (viewMode === 'project' && selectedProject) {
        params.set('teamId', selectedProject);
      }
      // If a team member is using Manager View (Team View allowed), fetch team-scoped tasks
      if (
        user &&
        user.role === 'team_member' &&
        (user as any).allowTeamView &&
        viewMode === 'manager'
      ) {
        const teamCtx = selectedTeam || user.teamId || '';
        if (teamCtx) params.set('teamId', teamCtx);
      }
      const url = `/api/tasks?${params.toString()}`;
      console.log(`[DEBUG] Fetching tasks for ${user?.role}: ${url}`);
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const data = await res.json();
      console.log(`[DEBUG] Received tasks response for ${user?.role}:`, {
        isArray: Array.isArray(data),
        hasItems: !!(data as any).items,
        itemsCount: Array.isArray(data)
          ? data.length
          : (data as any).items?.length || 0,
        firstFew: Array.isArray(data)
          ? data.slice(0, 3).map((t: any) => ({
              id: t.id,
              title: t.title?.substring(0, 30),
              teamId: t.teamId,
            }))
          : (data as any).items?.slice(0, 3).map((t: any) => ({
              id: t.id,
              title: t.title?.substring(0, 30),
              teamId: t.teamId,
            })),
      });
      return data;
    },
  });

  // Provide a stable alias to current tasks list to avoid ReferenceError in closures
  const tasks = taskPage.items;

  // Sync pagination state from query
  useEffect(() => {
    if (!tasksResp) {
      console.log(`[DEBUG] tasksResp is null/undefined for ${user?.role}`);
      return;
    }
    console.log(`[DEBUG] Processing tasksResp for ${user?.role}:`, {
      isArray: Array.isArray(tasksResp),
      hasItems: !!(tasksResp as any).items,
      rawType: typeof tasksResp,
    });
    if (Array.isArray(tasksResp)) {
      console.log(
        `[DEBUG] Setting taskPage.items to array of ${tasksResp.length} tasks`
      );
      setTaskPage({ items: tasksResp, nextCursor: null, isLoadingMore: false });
    } else if ((tasksResp as any).items) {
      const items = (tasksResp as any).items || [];
      console.log(
        `[DEBUG] Setting taskPage.items from paginated response: ${items.length} tasks`
      );
      setTaskPage({
        items: items,
        nextCursor: (tasksResp as any).nextCursor || null,
        isLoadingMore: false,
      });
    } else {
      console.warn(
        `[DEBUG] Unexpected tasksResp format for ${user?.role}:`,
        tasksResp
      );
      setTaskPage({ items: [], nextCursor: null, isLoadingMore: false });
    }
    setInitialLoaded(true);
  }, [tasksResp, user?.role]);

  // Handle scrolling to a task from completed tasks notification
  useEffect(() => {
    if (!initialLoaded || tasks.length === 0) return;

    const scrollToTaskId = sessionStorage.getItem('scrollToTaskId');
    if (!scrollToTaskId) return;

    // Remove from sessionStorage immediately to prevent multiple attempts
    sessionStorage.removeItem('scrollToTaskId');

    // Try to find and scroll to the task element
    let attempts = 0;
    const maxAttempts = 10; // Try for up to 1 second

    const scrollToTask = () => {
      attempts++;
      const taskElement = document.querySelector(
        `[data-task-id="${scrollToTaskId}"]`
      );

      if (taskElement) {
        // Scroll into view
        taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Highlight the task briefly
        taskElement.classList.add(
          'ring-4',
          'ring-blue-500',
          'ring-opacity-50',
          'transition-all',
          'duration-300'
        );
        setTimeout(() => {
          taskElement.classList.remove(
            'ring-4',
            'ring-blue-500',
            'ring-opacity-50',
            'transition-all',
            'duration-300'
          );
        }, 3000);
      } else if (attempts < maxAttempts) {
        // Try again after a short delay
        setTimeout(scrollToTask, 100);
      }
    };

    // Start trying to find the element after a brief delay
    setTimeout(scrollToTask, 300);
  }, [initialLoaded, tasks]);

  const loadMore = useCallback(async () => {
    if (viewMode === 'project') return; // no pagination in Project View
    if (!taskPage.nextCursor) return;

    // Prevent multiple simultaneous load more requests
    setTaskPage(prev => ({ ...prev, isLoadingMore: true }));

    const params = new URLSearchParams();
    if (boardFilter) params.set('filter', boardFilter);
    params.set('limit', '50');
    params.set('cursor', taskPage.nextCursor);
    if (
      user &&
      user.role === 'team_member' &&
      (user as any).allowTeamView &&
      viewMode === 'manager'
    ) {
      const teamCtx = selectedTeam || user.teamId || '';
      if (teamCtx) params.set('teamId', teamCtx);
    }
    // Use teamId instead of projectId since we're showing team projects (teams)
    if (selectedProject && viewMode === 'project') {
      params.set('teamId', selectedProject);
    }

    try {
      const res = await fetch(`/api/tasks?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.items) {
        setTaskPage(prev => {
          // Check for duplicates before adding new items
          const existingIds = new Set(prev.items.map(item => item.id));
          const newItems = data.items.filter(
            (item: TaskWithRelations) => !existingIds.has(item.id)
          );

          return {
            items: [...prev.items, ...newItems],
            nextCursor: data.nextCursor || null,
            isLoadingMore: false,
          };
        });
      }
    } catch (error) {
      console.error('Error loading more tasks:', error);
      setTaskPage(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [
    taskPage.nextCursor,
    boardFilter,
    viewMode,
    user?.id,
    user?.role,
    selectedTeam,
    selectedProject,
  ]);

  const { data: users = [], isLoading: usersLoading } = useQuery<
    UserWithTeam[]
  >({
    queryKey: ['/api/users'],
    retry: false,
  });

  // Separate query for SOS help users (all users regardless of permissions)
  const { data: sosUsers = [], isLoading: sosUsersLoading } = useQuery<
    UserWithTeam[]
  >({
    queryKey: ['/api/users/sos-help'],
    retry: false,
    enabled: !!user, // Only fetch when user is authenticated
  });

  // Fetch teams - used as "team projects" in project view
  const { data: teams = [], isLoading: teamsLoading } = useQuery<
    TeamWithMembers[]
  >({
    queryKey: ['/api/teams'],
    retry: false,
  });

  // Keep projects for backward compatibility (might be used elsewhere)
  const { data: projects = [], isLoading: projectsLoading } = useQuery<
    Project[]
  >({
    queryKey: ['/api/projects'],
    retry: false,
    enabled: false, // Disable by default since we use teams for project view
  });

  // Auto-pick a default staff member when switching into Staff View
  useEffect(() => {
    if (viewMode !== 'staff') return;
    if (selectedStaff) return;
    const firstStaff = users.find(u => u.role !== 'admin') || users[0];
    if (firstStaff) {
      setSelectedStaff(firstStaff.id);
    }
  }, [viewMode, selectedStaff, users]);

  // Persist manager's selected team across reloads
  useEffect(() => {
    if (user?.role === 'manager') {
      const key = `sb_manager_selected_team_${user.id}`; // scope per-manager to avoid cross-user leakage
      // Initialize from storage if not set yet
      if (!selectedTeam) {
        const stored = window.localStorage.getItem(key);
        if (stored && stored !== 'null' && stored !== 'undefined') {
          setSelectedTeam(stored);
          return;
        }
      }
      // If stored team is not in current list (e.g., switching managers), fall back to user's own team
      if (selectedTeam && !teams.find(t => t.id === selectedTeam)) {
        const fallback = user.teamId || teams[0]?.id || null;
        setSelectedTeam(fallback);
        if (fallback) window.localStorage.setItem(key, fallback);
        return;
      }
      // Default to the first team the manager manages (or their own team)
      if (!selectedTeam && teams && teams.length > 0) {
        const managedTeams = teams.filter(
          t => (t as any).manager?.id === user.id
        );
        const managedTeam =
          managedTeams[0] || teams.find(t => t.id === user.teamId);
        if (managedTeam?.id) setSelectedTeam(managedTeam.id);
      }
      // Store any change
      if (selectedTeam) {
        window.localStorage.setItem(key, selectedTeam);
      }
      // Clean up any old global key from previous builds
      try {
        window.localStorage.removeItem('sb_manager_selected_team');
      } catch {}
    }
  }, [user, teams, selectedTeam]);

  // Guard: if a selectedTeam exists that is not present in teams, reset to user's team immediately
  useEffect(() => {
    if (
      user?.role === 'manager' &&
      selectedTeam &&
      !teams.find(t => t.id === selectedTeam)
    ) {
      const key = `sb_manager_selected_team_${user.id}`;
      const fallback = user.teamId || teams[0]?.id || null;
      setSelectedTeam(fallback);
      if (fallback) window.localStorage.setItem(key, fallback);
    }
  }, [teams, selectedTeam, user]);

  const { data: taskStats } = useQuery({
    queryKey: ['/api/analytics/task-stats'],
    retry: false,
  });

  // Fetch tasks for specific staff member when in staff view (includes help tasks)
  const { data: staffSpecificTasks, isLoading: staffTasksLoading } = useQuery<
    TaskWithRelations[]
  >({
    queryKey: ['/api/users', selectedStaff, 'tasks'],
    enabled: (viewMode === 'staff' || viewMode === 'my') && !!selectedStaff,
    retry: false,
    // No polling: refetch only when WebSocket invalidates (any notification)
    refetchInterval: false,
    queryFn: async () => {
      if (!selectedStaff) return [] as TaskWithRelations[];
      const res = await fetch(`/api/users/${selectedStaff}/tasks`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return await res.json();
    },
  });

  // Fetch help tasks for all users in manager view
  const { data: allUsersHelpTasks, isLoading: allUsersHelpTasksLoading } =
    useQuery<Record<string, TaskWithRelations[]>>({
      queryKey: ['/api/users/help-tasks', viewMode, selectedTeam],
      enabled: viewMode === 'manager' && users.length > 0,
      retry: false,
      queryFn: async () => {
        const tasksByUser: Record<string, TaskWithRelations[]> = {};

        // Get the filtered users for manager view
        const managerUsers = users.filter(u => {
          if (user?.role === 'admin') {
            return u.role === 'manager';
          }
          if (selectedTeam === 'all') {
            return u.role !== 'admin';
          }
          if (!selectedTeam) {
            return u.id === user?.id;
          }
          const team = teams.find(t => t.id === selectedTeam);
          if (!team) return false;
          return (
            u.teamId === selectedTeam ||
            (u.role === 'manager' && team.manager && u.id === team.manager.id)
          );
        });

        // Fetch tasks for each user in parallel
        const promises = managerUsers.map(async user => {
          try {
            const res = await fetch(`/api/users/${user.id}/tasks`, {
              credentials: 'include',
            });
            if (res.ok) {
              const userTasks = await res.json();
              tasksByUser[user.id] = userTasks;
            } else {
              tasksByUser[user.id] = [];
            }
          } catch (error) {
            console.error(`Error fetching tasks for user ${user.id}:`, error);
            tasksByUser[user.id] = [];
          }
        });

        await Promise.all(promises);
        return tasksByUser;
      },
    });

  // Task assignment mutation
  const assignTaskMutation = useMutation({
    mutationFn: async ({
      taskId,
      assigneeId,
    }: {
      taskId: string;
      assigneeId: string;
    }) => {
      const assignUrl = '/api/tasks/' + taskId + '/assign';
      const assignBody = { assigneeId: assigneeId };
      await apiRequest('PATCH', assignUrl, assignBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });
      // Also invalidate staff-specific queries so boards refresh after assignment
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: 'Success',
        description: 'Task assigned successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to assign task',
        variant: 'destructive',
      });
    },
  });

  // Assign to me mutation
  const assignToMeMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!user) throw new Error('User not authenticated');
      const assignToMeUrl = '/api/tasks/' + taskId + '/assign';
      const assignToMeBody = { assigneeId: user.id };
      await apiRequest('PATCH', assignToMeUrl, assignToMeBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      if (user) {
        queryClient.invalidateQueries({
          queryKey: [`/api/users/${user.id}/tasks`],
        });
      }
      toast({
        title: 'Success',
        description: 'Task assigned to you successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to assign task to yourself',
        variant: 'destructive',
      });
    },
  });

  // Print handler
  const handlePrint = (printType: 'own' | 'full') => {
    // Store the print type for the print content
    const printContentElement = document.querySelector('.print-only');
    if (printContentElement) {
      printContentElement.setAttribute('data-print-type', printType);
    }

    // Add CSS classes to control what gets printed
    const printStyles = document.createElement('style');
    printStyles.textContent = `
      @media print {
        .no-print { display: none !important; }
        .print-only { display: block !important; }
        .main-board-content { display: none !important; }
        .sidebar { display: none !important; }
        .header { display: none !important; }
        button, .button, [role="button"] { display: none !important; }
        .modal, .overlay, [data-modal] { display: none !important; }
        [data-radix-portal] { display: none !important; }
        [data-state="open"] { display: none !important; }
        .fixed { display: none !important; }
        .absolute { position: static !important; }
        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
        .print-header { margin-bottom: 20px; text-align: center; }
        .print-header h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; }
        .print-staff-section { page-break-inside: avoid; margin-bottom: 20px; }
        .print-task { page-break-inside: avoid; margin-bottom: 8px; border: 1px solid #000; padding: 8px; min-height: 100px; width: 100%; }
        .print-task .font-medium { font-weight: bold; }
        .print-task .text-gray-900 { color: #000 !important; }
        .print-task .text-gray-600 { color: #333 !important; }
        .print-task .text-gray-500 { color: #666 !important; }
        .print-comment-box { flex-shrink: 0; width: 120px; border: 2px solid #000; border-radius: 6px; padding: 6px; background: #fff; margin-right: 8px; min-height: 80px; }
        .print-checkbox { font-size: 14px; font-weight: bold; text-align: center; margin-bottom: 4px; color: #000; border: 1px solid #000; width: 18px; height: 18px; margin: 0 auto 4px auto; display: flex; align-items: center; justify-content: center; }
        .print-comment-lines { font-size: 10px; line-height: 1.2; }
        .print-comment-line { margin-bottom: 2px; color: #000; border-bottom: 1px solid #ccc; height: 10px; }
        .print-task-content { display: flex; width: 100%; gap: 8px; }
        .print-task-main { flex: 1; min-width: 0; }
        .print-task-details { min-width: 120px; flex-shrink: 0; text-align: right; border-left: 1px solid #ddd; padding-left: 6px; }
        @page { margin: 0.5in; size: A4; }

        /* Filter sections based on print type */
        .print-only[data-print-type="own"] .print-full-only {
          display: none !important;
        }
        .print-only[data-print-type="full"] .print-own-only {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(printStyles);

    // Trigger print
    window.print();

    // Clean up styles after printing
    setTimeout(() => {
      if (document.head.contains(printStyles)) {
        document.head.removeChild(printStyles);
      }
    }, 1000);
  };

  // Toolbar: saved views and quick create
  const Toolbar = () => (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2">
      {/* <div className="flex items-center gap-2 w-full sm:w-auto">
        <Select value={selectedView} onValueChange={v => applySavedView(v)}>
          <SelectTrigger
            className="w-32 sm:w-48 lg:w-56"
            aria-label="Saved views"
          >
            <SelectValue placeholder="Views" />
          </SelectTrigger>
          <SelectContent>
            {savedViews.length === 0 ? (
              <SelectItem value="__none__" disabled>
                No saved views
              </SelectItem>
            ) : (
              savedViews.map(v => (
                <SelectItem key={v.name} value={v.name}>
                  {v.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={saveCurrentView}
          aria-label="Save current view"
          className="text-xs sm:text-sm"
        >
          <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Save view</span>
          <span className="sm:hidden">Save</span>
        </Button>
        {selectedView && (
          <Button
            variant="ghost"
            onClick={() => deleteSavedView(selectedView)}
            aria-label="Delete saved view"
            className="text-xs sm:text-sm"
          >
            <span className="hidden sm:inline">Delete view</span>
            <span className="sm:hidden">Delete</span>
          </Button>
        )}
      </div> */}
      <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-between sm:ml-auto">
        {/* WebSocket Connection Status */}
        <div className="flex items-center gap-1">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-500' : 'bg-red-500'
            }`}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
          <span className="text-xs text-gray-500 hidden sm:inline">
            {isConnected ? 'Live' : 'Offline'}
          </span>
        </div>

        {/* Show/Hide Completed Tasks Toggle */}
        <div className="flex items-center gap-1 sm:gap-2">
          <label className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">
            Show Completed:
          </label>
          <label className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 sm:hidden">
            Completed:
          </label>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              showCompletedTasks ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            onClick={() => setShowCompletedTasks(!showCompletedTasks)}
            aria-label="Toggle completed tasks visibility"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                showCompletedTasks ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-xs text-gray-500">
            {showCompletedTasks ? 'ON' : 'OFF'}
          </span>
        </div>

        {/* Show All Users Toggle */}
        <div className="flex items-center gap-1 sm:gap-2">
          <label className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">
            Show All Users:
          </label>
          <label className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 sm:hidden">
            All Users:
          </label>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              showAllUsers ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            onClick={() => setShowAllUsers(!showAllUsers)}
            aria-label="Toggle all users visibility"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                showAllUsers ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-xs text-gray-500">
            {showAllUsers ? 'ON' : 'OFF'}
          </span>
        </div>

        <NotificationBell
          onTaskClick={handleNotificationTaskClick}
          userRole={user?.role}
        />

        {/* Global Task Collapse/Expand Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAllTasksCollapsed(true)}
            className="p-1 sm:p-2 h-6 w-6 sm:h-8 sm:w-8"
            aria-label="Collapse all tasks"
            title="Collapse all tasks"
          >
            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAllTasksCollapsed(false)}
            className="p-1 sm:p-2 h-6 w-6 sm:h-8 sm:w-8"
            aria-label="Expand all tasks"
            title="Expand all tasks"
          >
            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>

        {/* {canManageStaff ? (
          <Button
            onClick={() => setIsStaffModalOpen(true)}
            className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm"
            aria-label="Add staff"
          >
            <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Staff</span>
            <span className="sm:hidden">Add</span>
          </Button>
        ) : null} */}
        {/* New Task button - Show for users who can create tasks */}
        {canCreateTasks && (
          <Button
            onClick={() => setIsTaskModalOpen(true)}
            aria-label="New task"
            className="text-xs sm:text-sm"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">New Task</span>
            <span className="sm:hidden">New</span>
          </Button>
        )}
        {taskPage.nextCursor && (
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={taskPage.isLoadingMore}
            aria-label="Load more tasks"
            className="text-xs sm:text-sm"
          >
            {taskPage.isLoadingMore ? 'Loading...' : 'Load more'}
          </Button>
        )}
      </div>
    </div>
  );

  // Show loading state while data is being fetched
  const isLoading =
    authLoading || tasksLoading || usersLoading || allUsersHelpTasksLoading;

  // Filter tasks based on current view and settings
  const filteredTasks = useMemo(() => {
    const tasks = taskPage.items || [];

    // Debug: Log tasks for GM to check isViewer flag
    if (user?.role === 'gm') {
      console.log('[GM Board Debug] Total tasks:', tasks.length);
      const viewerTasks = tasks.filter((t: any) => (t as any).isViewer);
      console.log(
        '[GM Board Debug] Tasks with isViewer=true:',
        viewerTasks.length
      );
      if (viewerTasks.length > 0) {
        console.log(
          '[GM Board Debug] Viewer tasks:',
          viewerTasks.map((t: any) => ({
            id: t.id,
            title: t.title,
            isViewer: t.isViewer,
            assignedTo: t.assignedTo,
          }))
        );
      }
    }

    let filtered: TaskWithRelations[] = [];

    // Project view: show all tasks for the selected team project regardless of role
    // Filter by teamId since tasks are now assigned to team projects (teams)
    if (viewMode === 'project' && selectedProject) {
      filtered = tasks.filter(task => task.teamId === selectedProject);
    } else if (viewMode === 'manager') {
      // Tasks are already restricted server-side to the manager's visible scope (team members + relevant unassigned)
      filtered = tasks;
    } else if ((viewMode === 'staff' || viewMode === 'my') && selectedStaff) {
      // Use user-specific tasks that include help tasks
      let list = (staffSpecificTasks as TaskWithRelations[]) || [];

      // Debug: Log all tasks before filtering for GM
      if (user?.role === 'gm' && viewMode === 'my') {
        console.log(
          '[GM Board Debug] Before filtering - Total tasks:',
          list.length
        );
        console.log('[GM Board Debug] Tasks breakdown:', {
          total: list.length,
          withIsViewerFlag: list.filter(
            (t: any) => (t as any).isViewer === true
          ).length,
          assignedToGM: list.filter((t: any) => t.assignedTo === user.id)
            .length,
          assignedToOthers: list.filter(
            (t: any) => t.assignedTo && t.assignedTo !== user.id
          ).length,
          sample: list.slice(0, 5).map((t: any) => ({
            id: t.id,
            title: t.title?.substring(0, 30),
            isViewer: (t as any).isViewer,
            assignedTo: t.assignedTo,
            isAssignedToGM: t.assignedTo === user.id,
          })),
        });
      }

      // For GM/PM/HOD users, filter based on view mode:
      // - My Tasks: tasks assigned to me
      // - Viewer Tasks: tasks where I'm a viewer but not assignee (GM only)
      // - Created by Me: tasks I created
      if (
        user &&
        ['gm', 'pm', 'hod', 'dpm'].includes(user.role) &&
        viewMode === 'my'
      ) {
        const originalLength = list.length;
        if (gmViewMode === 'viewer' && user.role === 'gm') {
          // Show only viewer tasks (where GM is a viewer but not assignee)
          list = list.filter((t: any) => {
            const isViewer = (t as any).isViewer === true;
            const notAssignedToGM = t.assignedTo !== user.id;
            return isViewer && notAssignedToGM;
          });
          console.log('[GM Board Debug] Filtered to viewer tasks:', {
            original: originalLength,
            filtered: list.length,
            mode: 'viewer',
          });
        } else if (gmViewMode === 'created') {
          // Tasks created by this user (GM/PM/HOD)
          list = list.filter((t: any) => {
            const creatorId =
              (t as any).creator?.id || (t as any).createdBy || null;
            return creatorId === user.id;
          });
          console.log('[Board Debug] Filtered to created-by-me tasks:', {
            original: originalLength,
            filtered: list.length,
            mode: 'created',
            role: user.role,
          });
        } else {
          // My Tasks: tasks assigned to this user (including common tasks via assigneeIds)
          list = list.filter((t: any) => {
            const assigneeIds = t.assigneeIds as string[] | undefined;
            if (Array.isArray(assigneeIds) && assigneeIds.length > 0) {
              return assigneeIds.includes(user.id);
            }
            return t.assignedTo === user.id;
          });
          console.log('[Board Debug] Filtered to my assigned tasks:', {
            original: originalLength,
            filtered: list.length,
            mode: 'my',
            role: user.role,
          });
        }
      }
      if (viewMode === 'my') {
        const now = new Date();
        const threeDaysFromNow = new Date(
          now.getTime() + 3 * 24 * 60 * 60 * 1000
        );
        if (myFilter === 'pending')
          list = list.filter(t => t.status === 'pending');
        else if (myFilter === 'started')
          list = list.filter(
            t => t.status === 'started' || t.status === 'reopened'
          );
        else if (myFilter === 'completed')
          list = list.filter(t => t.status === 'complete');
        // 'unassigned' handled separately in render branch
      }
      filtered = list;
    } else if (viewMode === 'project' && selectedProject) {
      // Filter by teamId since tasks are now assigned to team projects (teams)
      filtered = tasks.filter(task => task.teamId === selectedProject);
    } else if (viewMode === 'admin') {
      if (adminFilterType === 'manager' && adminSelectedManager) {
        // Show tasks for team members managed by the selected manager
        const manager = users.find(user => user.id === adminSelectedManager);
        if (manager?.teamId) {
          filtered = tasks.filter(
            task => task.assignee?.teamId === manager.teamId
          );
        }
      } else if (adminFilterType === 'staff' && adminSelectedStaff) {
        filtered = tasks.filter(task => task.assignedTo === adminSelectedStaff);
      } else if (adminFilterType === 'project' && adminSelectedProject) {
        // Filter by teamId since tasks are now assigned to team projects (teams)
        filtered = tasks.filter(task => task.teamId === adminSelectedProject);
      } else {
        // For admin 'all' view, show all tasks
        filtered = tasks;
      }
    } else if (user && user.role === 'team_member') {
      // For team members, show only their assigned tasks
      filtered = tasks.filter(task => task.assignedTo === user.id);
    } else {
      // Default: show all tasks
      filtered = tasks;
    }

    // Note: Completed tasks filtering is now handled in StaffColumn component
    // This ensures consistent behavior across all views

    return filtered;
  }, [
    taskPage.items,
    viewMode,
    selectedProject,
    selectedStaff,
    staffSpecificTasks,
    myFilter,
    user?.id,
    user?.role,
    selectedTeam,
    teams,
    adminFilterType,
    adminSelectedManager,
    adminSelectedStaff,
    adminSelectedProject,
    showCompletedTasks,
    gmViewMode, // Add gmViewMode to dependencies so filtering updates when dropdown changes
  ]);

  // Calculate context-specific stats based on current view and filtered tasks
  const contextStats = useMemo(() => {
    // Apply filters to align with what’s actually shown on the board
    // 1) Drop "dropped" tasks (no column for them)
    // 2) Respect the "showCompletedTasks" toggle (except for "my" view which already handles it)
    let tasksToAnalyze = filteredTasks.filter(
      task => task.status !== 'dropped'
    );
    if (!showCompletedTasks && viewMode !== 'my') {
      tasksToAnalyze = tasksToAnalyze.filter(
        task => task.status !== 'complete'
      );
    }

    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const inProgressStatuses = ['started'];

    const completed = tasksToAnalyze.filter(
      task => task.status === 'complete'
    ).length;
    const inProgress = tasksToAnalyze.filter(task =>
      inProgressStatuses.includes(task.status)
    ).length;

    // Avoid double counting between urgency buckets and in-progress/completed
    const remainingForUrgency = tasksToAnalyze.filter(
      task =>
        task.status !== 'complete' && !inProgressStatuses.includes(task.status)
    );

    const urgent = remainingForUrgency.filter(task => {
      const dueDate = new Date(task.dueDate);
      return dueDate < now; // Overdue = urgent
    }).length;

    const dueSoon = remainingForUrgency.filter(task => {
      const dueDate = new Date(task.dueDate);
      return dueDate >= now && dueDate <= threeDaysFromNow;
    }).length;

    const counted = completed + inProgress + urgent + dueSoon;
    const other = Math.max(tasksToAnalyze.length - counted, 0);

    return {
      total: tasksToAnalyze.length,
      urgent,
      dueSoon,
      inProgress,
      completed,
      other,
    };
  }, [filteredTasks, viewMode, showCompletedTasks]);

  // Compute manager debug info (frontend received tasks)
  const managerDebugInfo = useMemo(() => {
    if (viewMode !== 'manager') return null;
    const sample = (tasks || []).slice(0, 8).map(t => ({
      id: t.id,
      title: t.title,
      assignedTo: t.assignedTo,
      teamId: (t as any).teamId ?? null,
      projectId: t.projectId ?? null,
      status: t.status,
    }));
    return { total: taskPage.items.length, sample };
  }, [viewMode, taskPage.items]);

  // Filter users and tasks based on selected filters and view mode
  const filteredUsers = (() => {
    // Admin/Manager Team View: derive members directly from teams for reliability
    if (viewMode === 'admin_team' || viewMode === 'manager_team') {
      if (!selectedTeam) return [] as UserWithTeam[];
      const team = teams.find(t => t.id === selectedTeam);
      if (!team) return [] as UserWithTeam[];
      const members = (team as any).members || [];
      const manager = (team as any).manager ? [(team as any).manager] : [];
      // Exclude admins from member columns
      let filtered = [...manager, ...members].filter(
        (u: any, idx: number, arr: any[]) =>
          u &&
          u.role !== 'admin' &&
          arr.findIndex((x: any) => x.id === u.id) === idx
      );

      // Apply search filter if user is admin or GM and search query exists
      if (
        (user?.role === 'admin' || user?.role === 'gm') &&
        userSearchQuery.trim()
      ) {
        const query = userSearchQuery.toLowerCase().trim();
        filtered = filtered.filter((u: any) => {
          const fullName =
            `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
          return fullName.includes(query);
        });
      }

      return filtered;
    }

    return users.filter(u => {
      if (viewMode === 'staff') {
        return selectedStaff ? u.id === selectedStaff : false;
      }
      if (viewMode === 'my') {
        return u.id === user?.id;
      }

      // Project view for any role: show users who have tasks in the selected project (no staff/manager selector)
      if (viewMode === 'project' && selectedProject) {
        // Exclude admins from columns, typically they don't own tasks
        if (u.role === 'admin') return false;
        // Keep if this user has at least one task in the selected project
        return taskPage.items.some(
          task => task.projectId === selectedProject && task.assignedTo === u.id
        );
      }

      // Admin in Project view (no selected project): fallback to show everyone
      if (
        viewMode === 'project' &&
        user?.role === 'admin' &&
        !selectedProject
      ) {
        return u.role !== 'admin';
      }

      // Admin team view: show selected team members and that team's manager
      if (viewMode === 'admin_team') {
        if (!selectedTeam) return false;
        const team = teams.find(t => t.id === selectedTeam);
        return (
          u.teamId === selectedTeam ||
          (u.role === 'manager' &&
            team &&
            team.manager &&
            u.id === team.manager.id)
        );
      }

      // Manager view: show all members of the selected team AND the manager's own board
      if (viewMode === 'manager') {
        // Admin in Manager view: show only managers' personal boards
        if (user?.role === 'admin') {
          return u.role === 'manager';
        }
        // Team member with Manager View permission: show all teammates + their manager
        if (user?.role === 'team_member' && (user as any).allowTeamView) {
          const teamId = user.teamId;
          const isManagerOfTeam = u.role === 'manager' && u.teamId === teamId;
          return u.teamId === teamId || isManagerOfTeam;
        }
        if (selectedTeam === 'all') {
          // Show everyone except admins for an overview
          return u.role !== 'admin';
        }
        if (selectedTeam) {
          // include team members and the team manager
          const team = teams.find(t => t.id === selectedTeam);
          return (
            u.teamId === selectedTeam ||
            (u.role === 'manager' && u.id === team?.managerId)
          );
        }
        return u.role !== 'admin';
      }

      if (viewMode === 'admin') {
        if (adminFilterType === 'manager' && adminSelectedManager) {
          // Show users managed by selected manager (same team)
          const manager = users.find(user => user.id === adminSelectedManager);
          return manager?.teamId ? u.teamId === manager.teamId : false;
        }
        if (adminFilterType === 'staff' && adminSelectedStaff) {
          return u.id === adminSelectedStaff;
        }
        if (adminFilterType === 'project' && adminSelectedProject) {
          // Only show users who have tasks in the selected project
          return taskPage.items.some(
            task =>
              task.projectId === adminSelectedProject &&
              task.assignedTo === u.id
          );
        }
        // For 'all' or no specific filter, show all users (but exclude admins)
        return u.role !== 'admin';
      }

      // For team members, show only themselves
      if (user && user.role === 'team_member') {
        if ((user as any).allowTeamView && showTeamView) {
          return u.teamId === user.teamId; // show entire team
        }
        return u.id === user.id;
      }

      if (selectedFilter === 'team' && selectedTeam) {
        return u.teamId === selectedTeam;
      }
      if (selectedFilter === 'project' && selectedProject) {
        // Only show users who have tasks in the selected team project (filter by teamId)
        return taskPage.items.some(
          task => task.teamId === selectedProject && task.assignedTo === u.id
        );
      }
      return true;
    });
  })();

  // Apply search filter for admin and GM views
  const finalFilteredUsers = (() => {
    let users = filteredUsers;

    // Apply text search filter if userSearchQuery exists
    if (
      (user?.role === 'admin' || user?.role === 'gm') &&
      userSearchQuery.trim() &&
      (viewMode === 'admin' ||
        viewMode === 'admin_team' ||
        (user?.role === 'gm' && viewMode === 'my'))
    ) {
      const query = userSearchQuery.toLowerCase().trim();
      users = users.filter(u => {
        const fullName =
          `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        return fullName.includes(query);
      });
    }

    // Apply selected user IDs filter if any are selected
    if (selectedUserIds.length > 0) {
      users = users.filter(u => selectedUserIds.includes(u.id));
    }

    return users;
  })();

  // Group tasks by assignee, including help tasks for staff view and manager view.
  // For common/shared tasks, backend exposes `assigneeIds` so a task can
  // appear in multiple user columns on the board.
  const tasksByUser = finalFilteredUsers.reduce(
    (acc, user) => {
      if (viewMode === 'staff' && selectedStaff === user.id) {
        // For staff view, show all tasks for this specific user (including help tasks)
        acc[user.id] = filteredTasks;
      } else if (viewMode === 'manager' && allUsersHelpTasks) {
        // For manager view, use the complete task data including help tasks
        acc[user.id] = allUsersHelpTasks[user.id] || [];
      } else {
        // For other views, show tasks assigned to this user.
        // Prefer assigneeIds (for common tasks); fall back to assignedTo.
        acc[user.id] = filteredTasks.filter(task => {
          const assigneeIds = (task as any).assigneeIds as string[] | undefined;
          if (Array.isArray(assigneeIds) && assigneeIds.length > 0) {
            return assigneeIds.includes(user.id);
          }
          return task.assignedTo === user.id;
        });
      }
      return acc;
    },
    {} as Record<string, TaskWithRelations[]>
  );

  // Filter users to only show those with active tasks if showAllUsers is false
  // When showCompletedTasks is ON, also include users with completed tasks
  const usersToDisplay = (() => {
    if (showAllUsers) {
      return finalFilteredUsers;
    }
    // Show users with active tasks (not complete, pending_approval, or dropped)
    // If showCompletedTasks is ON, also include users with completed tasks
    return finalFilteredUsers.filter(user => {
      const userTasks = tasksByUser[user.id] || [];
      const activeTasks = userTasks.filter(
        (task: TaskWithRelations) =>
          !['complete', 'pending_approval', 'dropped'].includes(task.status)
      );

      // If user has active tasks, always show them
      if (activeTasks.length > 0) {
        return true;
      }

      // If showCompletedTasks is ON, also show users with completed tasks
      if (showCompletedTasks) {
        const completedTasks = userTasks.filter(
          (task: TaskWithRelations) =>
            task.status === 'complete' || task.status === 'pending_approval'
        );
        return completedTasks.length > 0;
      }

      // Otherwise, don't show this user
      return false;
    });
  })();

  const handleTaskDrop = (taskId: string, targetUserId: string) => {
    assignTaskMutation.mutate({ taskId, assigneeId: targetUserId });
  };

  const handleTaskView = (task: TaskWithRelations) => {
    setViewingTask(task);
    // Only set extension request mode for non-admins (users who can request extensions)
    // Admins will see the "Extend Due Date" button instead
    if (user?.role !== 'admin') {
      const now = new Date();
      const dueDate = new Date(task.dueDate);
      const isOverdue = dueDate < now;
      setRequestingExtension(isOverdue && task.status !== 'complete');
    } else {
      setRequestingExtension(false);
    }
  };

  const handleNotificationTaskClick = (taskId: string) => {
    // Find the task in the currently displayed list (filteredTasks) or main tasks list
    const task =
      filteredTasks.find((t: TaskWithRelations) => t.id === taskId) ||
      tasks.find((t: TaskWithRelations) => t.id === taskId);
    if (task) {
      handleTaskView(task);
    } else {
      toast({
        title: 'Task not in current view',
        description:
          'Switch to "My Tasks", "Created by Me", or another view that may show this task, then click the notification again.',
        variant: 'destructive',
      });
    }
  };

  const handleTaskEdit = (task: TaskWithRelations) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const handleAddTask = (userId: string) => {
    setPreselectedAssigneeId(userId);
    setEditingTask(null);
    setIsTaskModalOpen(true);
  };

  const handleTaskStatusUpdate = async (taskId: string, status: string) => {
    try {
      if (status === 'approve') {
        // Handle approval separately
        const approveUrl = '/api/tasks/' + taskId + '/approve';
        await apiRequest('PATCH', approveUrl, {});
        toast({
          title: 'Success',
          description: 'Task approved successfully',
        });
      } else if (status === 'disapprove') {
        // Handle disapproval separately
        const disapproveUrl = '/api/tasks/' + taskId + '/disapprove';
        await apiRequest('PATCH', disapproveUrl, {});
        toast({
          title: 'Success',
          description: 'Task disapproved successfully',
        });
      } else if (status === 'drop') {
        // Handle dropping separately
        const dropUrl = '/api/tasks/' + taskId + '/drop';
        const updatedTask = await apiRequest('PATCH', dropUrl, {});
        const isPendingApproval = false;
        toast({
          title: 'Success',
          description: isPendingApproval
            ? 'Drop request sent for approval'
            : 'Task dropped successfully',
        });
        // Immediately invalidate and refetch all task-related queries
        // This ensures all pages (including help-requests) get updated data
        queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
        queryClient.invalidateQueries({
          queryKey: ['/api/analytics/task-stats'],
        });
        queryClient.invalidateQueries({ queryKey: ['/api/users'] });
        // Invalidate all queries that might contain tasks
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === '/api/tasks' ||
              (query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks')),
        });
        // Explicitly refetch ALL matching queries (including inactive ones)
        await queryClient.refetchQueries({
          queryKey: ['/api/tasks'],
          type: 'all', // Refetch all queries, not just active ones
        });
        return; // Early return to avoid duplicate invalidation
      } else {
        const url = '/api/tasks/' + taskId;
        const body = { status: status };
        await apiRequest('PATCH', url, body);
        toast({
          title: 'Success',
          description: 'Task status updated successfully',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });
      // Also invalidate staff-specific queries so helper boards stay synchronized
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
    } catch (error: unknown) {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update task status',
        variant: 'destructive',
      });
    }
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setPreselectedAssigneeId(undefined);
  };

  const handleAssignToMe = (taskId: string) => {
    assignToMeMutation.mutate(taskId);
  };

  const handleNeedHelp = (task: TaskWithRelations) => {
    setNeedHelpTask(task);
  };

  const handleNeedHelpClose = () => {
    setNeedHelpTask(null);
  };

  const handleTaskPriorityUpdate = async (taskId: string, priority: number) => {
    try {
      console.log(
        'handleTaskPriorityUpdate: Updating task',
        taskId,
        'to priority',
        priority
      );
      const priorityUrl = '/api/tasks/' + taskId + '/priority';
      const priorityBody = { priority: priority };
      await apiRequest('PATCH', priorityUrl, priorityBody);
      console.log('handleTaskPriorityUpdate: API call successful');
      toast({
        title: 'Success',
        description: 'Task priority updated successfully',
      });
      // Refresh all task-related queries comprehensively
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });

      // Invalidate user-specific task queries (for manager personal view)
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === '/api/users' &&
          query.queryKey[2] === 'tasks',
      });

      // Invalidate all queries that start with '/api/tasks' (includes team-scoped queries)
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) && query.queryKey[0] === '/api/tasks',
      });

      console.log('handleTaskPriorityUpdate: All task queries invalidated');
    } catch (error: any) {
      console.error('Error updating task priority:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update task priority',
        variant: 'destructive',
      });
    }
  };

  const canManageStaff = user?.role === 'manager' || user?.role === 'admin';
  const canCreateTasks =
    user?.role === 'manager' ||
    user?.role === 'admin' ||
    user?.role === 'team_member' ||
    user?.role === 'gm' ||
    user?.role === 'pm' ||
    user?.role === 'hod' || user?.role === 'dpm';

  // Show loading screen if data is still being fetched
  if (isLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar collapsed={isSidebarCollapsed} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading board...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex h-screen bg-gray-50">
        <Sidebar
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <div className="flex-1 flex flex-col overflow-hidden main-board-content relative">
          {/* SURYACON Logo Watermark */}
          <div
            className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center"
            style={{
              opacity: 0.08,
            }}
          >
            <img
              src="/suryacon-logo.png"
              alt="SURYACON"
              className="w-[800px] h-auto"
            />
          </div>
          {/* Toolbar */}
          <Toolbar />

          {/* Top Bar */}
          <div className="bg-white shadow-sm border-b border-gray-200 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                <div className="min-w-0 flex-1">
                  <h2
                    className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate"
                    data-testid="text-page-title"
                  >
                    Task Board
                  </h2>
                  {viewMode === 'manager' && selectedTeam && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      Viewing {teams.find(t => t.id === selectedTeam)?.name}{' '}
                      team tasks
                    </p>
                  )}
                  {viewMode === 'staff' && selectedStaff && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      Viewing{' '}
                      {users.find(u => u.id === selectedStaff)?.firstName}{' '}
                      {users.find(u => u.id === selectedStaff)?.lastName}'s
                      tasks
                    </p>
                  )}
                  {viewMode === 'manager' && selectedTeam === 'all' && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      Viewing all teams (read-only)
                    </p>
                  )}
                  {viewMode === 'project' && selectedProject && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      Viewing{' '}
                      {teams.find(t => t.id === selectedProject)?.name ||
                        'Team Project'}{' '}
                      team project tasks
                    </p>
                  )}
                  {viewMode === 'admin' && (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      {adminFilterType === 'all'
                        ? 'Viewing all tasks'
                        : adminFilterType === 'manager' && adminSelectedManager
                          ? `Viewing tasks managed by ${users.find(u => u.id === adminSelectedManager)?.firstName} ${users.find(u => u.id === adminSelectedManager)?.lastName}`
                          : adminFilterType === 'staff' && adminSelectedStaff
                            ? `Viewing tasks for ${users.find(u => u.id === adminSelectedStaff)?.firstName} ${users.find(u => u.id === adminSelectedStaff)?.lastName}`
                            : adminFilterType === 'project' &&
                                adminSelectedProject
                              ? `Viewing ${projects.find(p => p.id === adminSelectedProject)?.name} project tasks`
                              : 'Admin dashboard - select a filter to view specific tasks'}
                    </p>
                  )}
                </div>

                {/* Help Button */}
                {/* {(user?.role === 'team_member' ||
                  user?.role === 'manager' ||
                  user?.role === 'admin') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsHelpRequestModalOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Request Help
                  </Button>
                )} */}

                {/* Print Button */}
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPrintDialogOpen(true)}
                  className="flex items-center gap-2"
                  aria-label="Print board"
                >
                  <Printer className="w-4 h-4" />
                  Print Board
                </Button> */}

                {/* User Filter Dropdown for Admin and GM */}
                {((user?.role === 'admin' &&
                  (viewMode === 'admin' || viewMode === 'admin_team')) ||
                  (user?.role === 'gm' && viewMode === 'my')) && (
                  <div className="flex items-center gap-2">
                    <DropdownMenu
                      open={isUserDropdownOpen}
                      onOpenChange={setIsUserDropdownOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-64 justify-start"
                        >
                          <Users className="mr-2 h-4 w-4" />
                          {selectedUserIds.length === 0
                            ? 'Select users...'
                            : selectedUserIds.length === 1
                              ? users.find(u => u.id === selectedUserIds[0])
                                ? `${users.find(u => u.id === selectedUserIds[0])?.firstName || ''} ${users.find(u => u.id === selectedUserIds[0])?.lastName || ''}`.trim()
                                : `${selectedUserIds.length} user selected`
                              : `${selectedUserIds.length} users selected`}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-64" align="start">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                              type="text"
                              placeholder="Search users..."
                              value={userDropdownSearch}
                              onChange={e =>
                                setUserDropdownSearch(e.target.value)
                              }
                              className="pl-8"
                              onClick={e => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {(() => {
                            // Filter users based on showAllUsers toggle
                            // Always use all users for the dropdown, but filter based on showAllUsers toggle
                            let usersToShow = users;
                            if (!showAllUsers) {
                              // Only show users with active tasks
                              // Get tasks for all users (not just finalFilteredUsers) to check for active tasks
                              usersToShow = users.filter(u => {
                                // Check if user has active tasks by looking at filteredTasks
                                const userActiveTasks = filteredTasks.filter(
                                  (task: TaskWithRelations) =>
                                    task.assignedTo === u.id &&
                                    ![
                                      'complete',
                                      'pending_approval',
                                      'dropped',
                                    ].includes(task.status)
                                );
                                return userActiveTasks.length > 0;
                              });
                            }
                            // Apply search filter
                            return usersToShow.filter(u => {
                              if (!userDropdownSearch.trim()) return true;
                              const query = userDropdownSearch
                                .toLowerCase()
                                .trim();
                              const fullName =
                                `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                              return fullName.includes(query);
                            });
                          })().map(u => (
                            <DropdownMenuCheckboxItem
                              key={u.id}
                              checked={selectedUserIds.includes(u.id)}
                              onCheckedChange={checked => {
                                if (checked) {
                                  setSelectedUserIds([
                                    ...selectedUserIds,
                                    u.id,
                                  ]);
                                } else {
                                  setSelectedUserIds(
                                    selectedUserIds.filter(id => id !== u.id)
                                  );
                                }
                              }}
                              onSelect={e => {
                                e.preventDefault();
                              }}
                            >
                              {`${u.firstName || ''} ${u.lastName || ''}`.trim() ||
                                'Unknown User'}
                            </DropdownMenuCheckboxItem>
                          ))}
                          {(() => {
                            // Filter users based on showAllUsers toggle
                            // Always use all users for the dropdown, but filter based on showAllUsers toggle
                            let usersToShow = users;
                            if (!showAllUsers) {
                              // Only show users with active tasks
                              // Get tasks for all users (not just finalFilteredUsers) to check for active tasks
                              usersToShow = users.filter(u => {
                                // Check if user has active tasks by looking at filteredTasks
                                const userActiveTasks = filteredTasks.filter(
                                  (task: TaskWithRelations) =>
                                    task.assignedTo === u.id &&
                                    ![
                                      'complete',
                                      'pending_approval',
                                      'dropped',
                                    ].includes(task.status)
                                );
                                return userActiveTasks.length > 0;
                              });
                            }
                            // Apply search filter
                            return usersToShow.filter(u => {
                              if (!userDropdownSearch.trim()) return false;
                              const query = userDropdownSearch
                                .toLowerCase()
                                .trim();
                              const fullName =
                                `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                              return fullName.includes(query);
                            });
                          })().length === 0 &&
                            userDropdownSearch.trim() && (
                              <div className="px-2 py-4 text-sm text-gray-500 text-center">
                                No users found
                              </div>
                            )}
                        </div>
                        {selectedUserIds.length > 0 && (
                          <div className="p-2 border-t">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full"
                              onClick={() => {
                                setSelectedUserIds([]);
                                setUserDropdownSearch('');
                              }}
                            >
                              Clear Selection
                            </Button>
                          </div>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {selectedUserIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUserIds([]);
                          setUserDropdownSearch('');
                        }}
                        className="h-8"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                )}

                {/* View Mode Dropdown */}
                <div className="flex items-center space-x-2">
                  {viewMode === 'manager' && (
                    <div
                      className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200"
                      title="Tasks received from server"
                    >
                      Tasks:{' '}
                      {(() => {
                        if (!tasks) return 0;
                        // If showCompletedTasks is OFF, count only active tasks
                        if (!showCompletedTasks) {
                          return tasks.filter(
                            task =>
                              ![
                                'complete',
                                'pending_approval',
                                'dropped',
                              ].includes(task.status)
                          ).length;
                        }
                        // If showCompletedTasks is ON, count all tasks
                        return tasks.length;
                      })()}
                    </div>
                  )}
                  {/* View Mode Dropdown - Hidden for admins (only Admin Dashboard available), GM/PM/HOD, and Staff */}
                  {user?.role !== 'admin' &&
                  !['gm', 'pm', 'hod', 'dpm', 'staff'].includes(
                    user?.role || ''
                  ) ? (
                    <Select
                      value={viewMode}
                      onValueChange={(
                        value:
                          | 'manager'
                          | 'manager_team'
                          | 'staff'
                          | 'project'
                          | 'admin'
                          | 'my'
                          | 'admin_team'
                      ) => {
                        // Prevent team members without permission from switching to Manager View
                        if (
                          value === 'manager' &&
                          user?.role === 'team_member' &&
                          !(user as any).allowTeamView
                        ) {
                          return; // ignore selection
                        }
                        if (value === 'my' && user) {
                          setSelectedStaff(user.id);
                        }
                        if (
                          value === 'admin_team' ||
                          value === 'manager_team'
                        ) {
                          // Reset selectors to avoid stale context
                          setSelectedTeam(null);
                          setSelectedProject(null);
                          setSelectedStaff(null);
                        }
                        setViewMode(value as any);
                      }}
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(user?.role !== 'team_member' ||
                          (user as any).allowTeamView) && (
                          <SelectItem value="manager">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              Manager View
                            </div>
                          </SelectItem>
                        )}
                        {/* Manager Team View (Manager) */}
                        {user?.role === 'manager' && (
                          <SelectItem value="manager_team">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              Team View (Manager)
                            </div>
                          </SelectItem>
                        )}
                        {(user?.role === 'manager' ||
                          user?.role === 'team_member') && (
                          <SelectItem value="my">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              My Board
                            </div>
                          </SelectItem>
                        )}
                        {user?.role !== 'team_member' &&
                          !['gm', 'pm', 'hod', 'dpm'].includes(
                            user?.role || ''
                          ) && (
                            <SelectItem value="staff">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 mr-2" />
                                Staff View
                              </div>
                            </SelectItem>
                          )}
                        {!['gm', 'pm', 'hod', 'dpm'].includes(
                          user?.role || ''
                        ) && (
                          <SelectItem value="project">
                            <div className="flex items-center">
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Project View
                            </div>
                          </SelectItem>
                        )}
                        {user?.role === 'admin' && (
                          <>
                            <SelectItem value="admin">
                              <div className="flex items-center">
                                <LayoutGrid className="w-4 h-4 mr-2" />
                                Admin Dashboard
                              </div>
                            </SelectItem>
                            <SelectItem value="my">
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-2" />
                                Personal Board
                              </div>
                            </SelectItem>
                            <SelectItem value="admin_team">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 mr-2" />
                                Team View (Admin)
                              </div>
                            </SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  ) : // For GM/PM/HOD, show dropdown to switch between views
                  ['gm', 'pm', 'hod', 'dpm'].includes(user?.role || '') ? (
                    <Select
                      value={gmViewMode}
                      onValueChange={value =>
                        setGmViewMode(value as 'my' | 'viewer' | 'created')
                      }
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="my">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2" />
                            My Tasks
                          </div>
                        </SelectItem>
                        {user?.role === 'gm' && (
                          <SelectItem value="viewer">
                            <div className="flex items-center">
                              <Eye className="w-4 h-4 mr-2" />
                              Viewer Tasks
                            </div>
                          </SelectItem>
                        )}
                        <SelectItem value="created">
                          <div className="flex items-center">
                            <FolderOpen className="w-4 h-4 mr-2" />
                            Created by Me
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                      <Users className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        My Board
                      </span>
                    </div>
                  )}

                  {/* Team selector: visible for Admin Team View */}
                  {viewMode === 'admin_team' && (
                    <Select
                      value={selectedTeam || undefined}
                      onValueChange={v => setSelectedTeam(v)}
                    >
                      <SelectTrigger className="w-64" aria-label="Select team">
                        <SelectValue placeholder="Select a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {(teams || []).map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Team selector: visible for Manager Team View */}
                  {viewMode === 'manager_team' &&
                    teams.filter(t => (t as any).manager?.id === user?.id)
                      .length > 0 && (
                      <Select
                        value={selectedTeam || undefined}
                        onValueChange={v => setSelectedTeam(v)}
                      >
                        <SelectTrigger
                          className="w-64"
                          aria-label="Select team"
                        >
                          <SelectValue placeholder="Select a team" />
                        </SelectTrigger>
                        <SelectContent>
                          {teams
                            .filter(t => (t as any).manager?.id === user?.id)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(t => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}
                              </SelectItem>
                            ))}
                          <SelectItem value="all">
                            All teams (read-only)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                  {/* Team Project selector (applies to staff too). Visible only in Project View */}
                  {viewMode === 'project' && (
                    <Select
                      value={selectedProject || undefined}
                      onValueChange={v => setSelectedProject(v)}
                    >
                      <SelectTrigger
                        className="w-64"
                        aria-label="Select team project"
                      >
                        <SelectValue placeholder="Select a team project" />
                      </SelectTrigger>
                      <SelectContent>
                        {(teams || [])
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Staff selector (visible in Staff View) */}
                  {viewMode === 'staff' && (
                    <Select
                      value={selectedStaff || undefined}
                      onValueChange={v => setSelectedStaff(v)}
                    >
                      <SelectTrigger className="w-64" aria-label="Select staff">
                        <SelectValue placeholder="Select a staff member" />
                      </SelectTrigger>
                      <SelectContent>
                        {(users || [])
                          .filter(u => u.role !== 'admin')
                          .sort((a, b) =>
                            `${a.firstName} ${a.lastName}`.localeCompare(
                              `${b.firstName} ${b.lastName}`
                            )
                          )
                          .map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName} {u.lastName || ''}
                              {u.role === 'manager' ? ' (Manager)' : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Manager/Admin Personal toggle */}
                  {(user?.role === 'manager' || user?.role === 'admin') && (
                    <div
                      className="ml-2 inline-flex rounded-md shadow-sm"
                      role="group"
                    >
                      <button
                        type="button"
                        className={`px-3 py-1.5 text-xs font-medium border ${boardPersonal ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50' : 'bg-blue-600 text-white border-blue-600'}`}
                        onClick={() => setBoardPersonal(false)}
                        title="Show all visible tasks"
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-1.5 text-xs font-medium border -ml-px ${boardPersonal ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                        onClick={() => setBoardPersonal(true)}
                        title="Show only my tasks (and help requests)"
                      >
                        Me
                      </button>
                    </div>
                  )}
                </div>

                {/* Simplified top bar controls retained above; admin filters removed for now */}
              </div>

              {/* Action buttons removed to avoid duplication with the Toolbar. */}

              {/* Manager View Debug Overlay (temporary) */}
              {viewMode === 'manager' &&
                showManagerDebug &&
                managerDebugInfo &&
                new URLSearchParams(window.location.search).get('debug') !==
                  '0' && (
                  <div className="mt-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium">
                        Manager Debug: frontend received{' '}
                        {managerDebugInfo.total} tasks
                      </div>
                      <button
                        className="text-amber-800 underline"
                        onClick={() => setShowManagerDebug(false)}
                      >
                        hide
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap break-words max-h-40 overflow-auto">
                      {JSON.stringify(managerDebugInfo.sample, null, 2)}
                    </pre>
                  </div>
                )}
            </div>

            {/* Team Information for Staff */}
            {/* {user?.role === 'team_member' && (
              <div className="mt-4 bg-blue-50 dark:bg-blue-900 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Your Team Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <div className="font-medium text-blue-700 dark:text-blue-300">
                      Your Manager:
                    </div>
                    {(() => {
                      // First check if the team has a specific manager assigned
                      const userTeam = teams.find(t => t.id === user.teamId);
                      const manager =
                        userTeam?.manager ||
                        users.find(
                          u => u.role === 'manager' && u.teamId === user.teamId
                        );
                      return manager ? (
                        <div className="text-blue-600 dark:text-blue-400">
                          {manager.firstName} {manager.lastName}
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400">
                          No manager assigned
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <div className="font-medium text-blue-700 dark:text-blue-300">
                      System Admins:
                    </div>
                    {(() => {
                      const admins = users.filter(u => u.role === 'admin');
                      return admins.length > 0 ? (
                        <div className="text-blue-600 dark:text-blue-400">
                          {admins
                            .map(
                              admin => `${admin.firstName} ${admin.lastName}`
                            )
                            .join(', ')}
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400">
                          No admins found
                        </div>
                      );
                    })()}
                  </div>
                  <div>
                    <div className="font-medium text-blue-700 dark:text-blue-300">
                      Your Team:
                    </div>
                    {user.teamId ? (
                      <div className="text-blue-600 dark:text-blue-400">
                        {teams.find(t => t.id === user.teamId)?.name ||
                          'Unknown Team'}
                      </div>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400">
                        No team assigned
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )} */}

            {/* Stats Bar */}
            {(viewMode === 'staff' && selectedStaff) ||
            (viewMode === 'my' && selectedStaff) ||
            (viewMode === 'project' && selectedProject) ? (
              <div className="mt-4 grid grid-cols-6 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Total Tasks</div>
                  <div
                    className="text-xl font-bold text-gray-900"
                    data-testid="stat-total-tasks"
                  >
                    {contextStats.total || 0}
                  </div>
                </div>
                {/* <div className="bg-red-50 p-3 rounded-lg">
                  <div className="text-sm text-red-600">Urgent</div>
                  <div
                    className="text-xl font-bold text-red-700"
                    data-testid="stat-urgent-tasks"
                  >
                    {contextStats.urgent || 0}
                  </div>
                </div> */}
                <div className="bg-orange-50 p-3 rounded-lg">
                  <div className="text-sm text-orange-600">Due Soon</div>
                  <div
                    className="text-xl font-bold text-orange-700"
                    data-testid="stat-due-soon-tasks"
                  >
                    {contextStats.dueSoon || 0}
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-sm text-blue-600">In Progress</div>
                  <div
                    className="text-xl font-bold text-blue-700"
                    data-testid="stat-in-progress-tasks"
                  >
                    {contextStats.inProgress || 0}
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-sm text-green-600">Completed</div>
                  <div
                    className="text-xl font-bold text-green-700"
                    data-testid="stat-completed-tasks"
                  >
                    {contextStats.completed || 0}
                  </div>
                </div>
                {/* <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Other</div>
                  <div className="text-xl font-bold text-gray-900">
                    {contextStats.other || 0}
                  </div>
                </div> */}
              </div>
            ) : null}
          </div>

          {/* Board View */}
          {viewMode === 'manager' ||
          viewMode === 'manager_team' ||
          viewMode === 'admin_team' ||
          (viewMode === 'project' && user?.role !== 'team_member') ||
          viewMode === 'admin' ? (
            // Manager View, Admin Team View, Project View (for managers/admins), and Admin View - Staff Columns Layout
            <div className="flex-1 overflow-x-auto overflow-y-hidden">
              <div className="flex h-full gap-1 sm:gap-2 min-w-0">
                {/* Staff Columns */}
                {usersToDisplay.map(staffMember => (
                  <StaffColumn
                    key={staffMember.id}
                    user={staffMember}
                    tasks={tasksByUser[staffMember.id] || []}
                    onTaskDrop={handleTaskDrop}
                    currentUser={
                      {
                        ...user!,
                        ...(((viewMode === 'manager' ||
                          viewMode === 'manager_team' ||
                          viewMode === 'admin_team') &&
                          (selectedTeam === 'all' || user?.role === 'admin')) ||
                        (user?.role === 'team_member' &&
                          (user as any).allowTeamView &&
                          showTeamView)
                          ? { readOnly: true }
                          : {}),
                      } as any
                    }
                    onTaskStatusUpdate={handleTaskStatusUpdate}
                    onTaskEdit={handleTaskEdit}
                    onTaskView={handleTaskView}
                    onAddTask={handleAddTask}
                    onNeedHelp={handleNeedHelp}
                    onAssignToMe={handleAssignToMe}
                    onPriorityUpdate={handleTaskPriorityUpdate}
                    readOnly={
                      ((viewMode === 'manager' ||
                        viewMode === 'manager_team' ||
                        viewMode === 'admin_team') &&
                        selectedTeam === 'all') ||
                      (user?.role === 'team_member' &&
                        (user as any).allowTeamView &&
                        showTeamView)
                    }
                    globalCollapsed={allTasksCollapsed}
                    showCompletedTasks={showCompletedTasks}
                  />
                ))}
              </div>
            </div>
          ) : (
            // Staff View and Project View (for team members) - Status Columns Layout
            <div className="flex-1 overflow-x-auto overflow-y-hidden">
              {selectedStaff ? (
                <div className="flex h-full gap-1 sm:gap-2 min-w-0">
                  {['pending', 'started', 'complete'].map(status => {
                    // In staff status view, exclude unassigned tasks from status columns. "started" column includes reopened.
                    const statusTasks = filteredTasks.filter(task => {
                      if (!task.assignedTo) return false;
                      if (status === 'started')
                        return (
                          task.status === 'started' ||
                          task.status === 'reopened'
                        );
                      return task.status === status;
                    });
                    const statusLabels = {
                      pending: 'Pending',
                      started: 'Started',
                      complete: 'Complete',
                    };
                    const statusColors = {
                      pending: 'bg-gray-100 border-gray-300',
                      started: 'bg-blue-100 border-blue-300',
                      complete: 'bg-green-100 border-green-300',
                    };

                    return (
                      <div
                        key={status}
                        className="flex flex-col w-72 sm:w-80 lg:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
                      >
                        {/* Status Header */}
                        <div
                          className={`p-2 sm:p-4 border-b border-gray-200 ${statusColors[status as keyof typeof statusColors]}`}
                        >
                          <h3 className="font-semibold text-gray-900 flex items-center justify-between text-sm sm:text-base">
                            <span className="truncate">
                              {
                                statusLabels[
                                  status as keyof typeof statusLabels
                                ]
                              }
                            </span>
                            <Badge
                              variant="secondary"
                              className="ml-2 text-xs flex-shrink-0"
                            >
                              {statusTasks.length}
                            </Badge>
                          </h3>
                        </div>

                        {/* Tasks List */}
                        <TaskDropZone
                          onTaskDrop={handleTaskPriorityUpdate}
                          className="flex-1 overflow-y-auto p-1 sm:p-2 space-y-1 sm:space-y-2"
                        >
                          {statusTasks
                            .sort(
                              (a, b) => (a.priority || 0) - (b.priority || 0)
                            )
                            .map((task, index) => (
                              <TaskCard
                                key={task.id}
                                task={task}
                                currentUser={user}
                                isHelpTask={(task as any).isHelpTask}
                                onStatusUpdate={handleTaskStatusUpdate}
                                onEdit={handleTaskEdit}
                                onView={handleTaskView}
                                onNeedHelp={handleNeedHelp}
                                onAssignToMe={handleAssignToMe}
                                onPriorityUpdate={handleTaskPriorityUpdate}
                                globalCollapsed={allTasksCollapsed}
                                isDraggable={true}
                                taskNumber={index + 1}
                                hasUnreadComments={hasUnreadComments(task.id)}
                              />
                            ))}
                          {statusTasks.length === 0 && (
                            <div className="text-center py-8 text-gray-500 text-sm">
                              No{' '}
                              {statusLabels[
                                status as keyof typeof statusLabels
                              ].toLowerCase()}{' '}
                              tasks
                            </div>
                          )}
                        </TaskDropZone>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-gray-500 text-lg">
                      Please select a staff member to view their tasks.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={handleCloseTaskModal}
          // For GM/PM/HOD task creation, use SOS users (unrestricted) so assignee dropdowns never go empty
          // The modal still filters by role/city/hierarchy on the client side.
          users={
            ['gm', 'pm', 'hod', 'dpm'].includes(user?.role || '')
              ? sosUsers.length > 0
                ? sosUsers
                : users
              : users
          }
          teams={teams}
          editTask={editingTask}
          preselectedAssigneeId={preselectedAssigneeId}
          currentUser={user}
        />

        <TaskViewModal
          isOpen={!!viewingTask}
          onClose={() => {
            setViewingTask(null);
            setRequestingExtension(false);
          }}
          task={viewingTask}
          currentUser={user}
          requestExtension={requestingExtension}
        />

        {needHelpTask && !sosUsersLoading && sosUsers.length > 0 && (
          <NeedHelpModal
            key={`sos-${needHelpTask.id}`}
            isOpen={!!needHelpTask}
            onClose={() => setNeedHelpTask(null)}
            taskId={needHelpTask.id}
            taskTitle={needHelpTask.title}
            availableUsers={sosUsers.filter(
              u =>
                u.id !== needHelpTask.assignedTo &&
                (u.role === 'admin' ||
                  u.role === 'manager' ||
                  u.role === 'team_member')
            )}
          />
        )}

        {canManageStaff && (
          <StaffModal
            isOpen={isStaffModalOpen}
            onClose={() => setIsStaffModalOpen(false)}
            teams={teams}
          />
        )}

        <HelpRequestModal
          isOpen={isHelpRequestModalOpen}
          onClose={() => setIsHelpRequestModalOpen(false)}
        />
      </div>

      {/* Print Content - Hidden by default, shown when printing */}
      <div className="print-only" style={{ display: 'none' }}>
        <div className="print-header">
          <h1>StaffBoard Task Report</h1>
          <p>
            Generated on {new Date().toLocaleDateString()} at{' '}
            {new Date().toLocaleTimeString()}
          </p>
          <p>Report Type: {selectedView || 'Current View'}</p>
        </div>

        <div className="print-content">
          {/* Own Tasks Only */}
          <div className="print-own-only">
            {user &&
              (() => {
                // Get user's tasks directly from filteredTasks, not from tasksByUser
                const userTasks = filteredTasks.filter(
                  task => task.assignedTo === user.id
                );
                return (
                  <div className="print-staff-section mb-6">
                    <h2 className="text-lg font-semibold mb-3 border-b pb-1">
                      {user.firstName} {user.lastName}'s Tasks
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        ({userTasks.length} task
                        {userTasks.length !== 1 ? 's' : ''})
                      </span>
                    </h2>

                    <div className="print-tasks">
                      {userTasks
                        .filter(task => !(task as any).isHelpTask)
                        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                        .map(task => (
                          <div
                            key={`print-own-task-${task.id}`}
                            className="print-task"
                          >
                            {/* Comment Box */}
                            <div className="print-comment-box">
                              <div className="print-checkbox">□</div>
                              <div className="print-comment-lines">
                                <div className="print-comment-line"></div>
                                <div className="print-comment-line"></div>
                                <div className="print-comment-line"></div>
                              </div>
                            </div>

                            {/* Task Main Content */}
                            <div className="print-task-main">
                              <div className="print-task-title">
                                <span className="print-priority-badge">
                                  #{(task.priority || 0) + 1}
                                </span>
                                {task.title}
                              </div>
                              <div className="print-task-assignee">
                                {task.assignedToUser
                                  ? `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`
                                  : 'Unassigned'}
                              </div>
                            </div>

                            {/* Task Details */}
                            <div className="print-task-details">
                              <div className="print-task-status">
                                {(() => {
                                  switch (task.status) {
                                    case 'pending':
                                      return 'PENDING';
                                    case 'started':
                                      return 'STARTED';
                                    case 'complete':
                                      return 'COMPLETE';
                                    default:
                                      return task.status.toUpperCase();
                                  }
                                })()}
                              </div>
                              <div className="print-task-date">
                                {new Date(task.dueDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })()}
          </div>

          {/* Full Board - Organized by Status */}
          <div className="print-full-only">
            {/* Group tasks by status for better organization */}
            {(() => {
              const allTasks =
                tasks?.filter(task => !(task as any).isHelpTask) || [];
              const tasksByStatus = {
                pending: allTasks.filter(
                  task => task.assignedTo && task.status === 'pending'
                ),
                started: allTasks.filter(
                  task =>
                    task.assignedTo &&
                    (task.status === 'started' || task.status === 'reopened')
                ),
                complete: allTasks.filter(
                  task => task.assignedTo && task.status === 'complete'
                ),
              };

              const statusSections = [
                {
                  key: 'pending',
                  title: 'Pending Tasks',
                  tasks: tasksByStatus.pending,
                },
                {
                  key: 'started',
                  title: 'Started Tasks',
                  tasks: tasksByStatus.started,
                },
                {
                  key: 'complete',
                  title: 'Completed Tasks',
                  tasks: tasksByStatus.complete,
                },
              ];

              return statusSections.map(section => {
                if (section.tasks.length === 0) return null;

                return (
                  <div
                    key={`print-status-${section.key}`}
                    className="print-status-section mb-8"
                  >
                    <h2 className="text-xl font-bold mb-4 border-b-2 border-gray-800 pb-2">
                      {section.title}
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        ({section.tasks.length} task
                        {section.tasks.length !== 1 ? 's' : ''})
                      </span>
                    </h2>

                    <div className="print-tasks">
                      {section.tasks
                        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                        .map(task => (
                          <div
                            key={`print-task-${task.id}`}
                            className="print-task"
                          >
                            {/* Comment Box */}
                            <div className="print-comment-box">
                              <div className="print-checkbox">□</div>
                              <div className="print-comment-lines">
                                <div className="print-comment-line"></div>
                                <div className="print-comment-line"></div>
                                <div className="print-comment-line"></div>
                              </div>
                            </div>

                            {/* Task Main Content */}
                            <div className="print-task-main">
                              <div className="print-task-title">
                                <span className="print-priority-badge">
                                  #{(task.priority || 0) + 1}
                                </span>
                                {task.title}
                              </div>
                              <div className="print-task-assignee">
                                {task.assignedToUser
                                  ? `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`
                                  : 'Unassigned'}
                              </div>
                            </div>

                            {/* Task Details */}
                            <div className="print-task-details">
                              <div className="print-task-status">
                                {(() => {
                                  switch (task.status) {
                                    case 'pending':
                                      return 'PENDING';
                                    case 'started':
                                      return 'STARTED';
                                    case 'working':
                                      return 'WORKING';
                                    case 'need_more_time':
                                      return 'NEED MORE TIME';
                                    case 'need_help':
                                      return 'NEED HELP';
                                    case 'help_done':
                                      return 'HELP DONE';
                                    case 'pending_approval':
                                      return 'PENDING APPROVAL';
                                    case 'complete':
                                      return 'COMPLETE';
                                    case 'dropped':
                                      return 'DROPPED';
                                    default:
                                      return task.status.toUpperCase();
                                  }
                                })()}
                              </div>
                              <div className="print-task-date">
                                {new Date(task.dueDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Modals */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setPreselectedAssigneeId(undefined);
        }}
        editTask={editingTask}
        preselectedAssigneeId={preselectedAssigneeId}
        teams={teams}
        users={users}
        currentUser={user}
      />

      <TaskViewModal
        isOpen={!!viewingTask}
        onClose={() => {
          setViewingTask(null);
          setRequestingExtension(false);
        }}
        task={viewingTask}
        currentUser={user}
        requestExtension={requestingExtension}
      />

      <HelpRequestModal
        isOpen={isHelpRequestModalOpen}
        onClose={() => setIsHelpRequestModalOpen(false)}
        users={users}
        teams={teams}
      />

      <NeedHelpModal
        isOpen={!!needHelpTask}
        onClose={() => setNeedHelpTask(null)}
        task={needHelpTask}
        users={users}
      />

      <StaffModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        teams={teams}
      />

      <PrintDialog
        isOpen={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        onPrint={handlePrint}
        userRole={user?.role || 'team_member'}
        userName={`${user?.firstName} ${user?.lastName}`}
      />
    </DndProvider>
  );
}
